import { ActionButton, CustomInput } from '@tcweb/connect-ui';
import {
  Component,
  createEffect,
  onMount,
  Show,
  on,
  onCleanup,
} from 'solid-js';
import { createSignal } from 'solid-js';
import { t } from '@trimble/i18n';

export interface EditableTitleProps {
  label?: string;
  handleEditLabelSave(label: string | undefined): void;
  hideTitleEdit?: boolean;
  disableEdit?: boolean;
  disabledReason?: string;
}

export const EditableTitle: Component<EditableTitleProps> = (props) => {
  const [isEditingTitle, setIsEditingTitle] = createSignal(false);
  const [editingLabel, setEditingLabel] = createSignal<string | undefined>(
    undefined
  );
  const [inputRef, setInputRef] = createSignal<HTMLInputElement | undefined>();

  onMount(() => {
    setEditingLabel(props.label);
  });

  createEffect(
    on(
      () => props.label,
      (label: string | undefined) => {
        if (!isEditingTitle()) {
          setEditingLabel(label);
        }
      }
    )
  );

  const handleEditButtonClick = (): void => {
    setEditingLabel(props.label);
    setIsEditingTitle(true);
  };

  const handleLabelChange = (e: Event): void => {
    const target = e.target as HTMLInputElement;
    setEditingLabel(target.value);
  };

  const handleLabelInputBlur = (): void => {
    setIsEditingTitle(false);
    props.handleEditLabelSave(editingLabel());
  };

  const handleEnterKeyPress = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      inputRef()?.blur();
    }
  };

  const addKeyDownListener = (el: HTMLInputElement | undefined): void => {
    setInputRef(el);
    el?.addEventListener('keydown', handleEnterKeyPress);

    onCleanup(() => {
      el?.removeEventListener('keydown', handleEnterKeyPress);
    });
  };

  createEffect(() => {
    if (inputRef()) {
      inputRef()?.focus();
      inputRef()?.select();
    }
  });

  return (
    <Show
      when={!isEditingTitle()}
      fallback={
        <div class="flex-1 min-w-0 flex items-center gap-2 px-2">
          <CustomInput
            name="edit-label"
            label=""
            dataCy="measurement-name-edit"
            placeholder={props.label ?? t('measurements.measurementData')}
            class="flex-1 min-w-0 [&_input]:!outline-none [&_input]:!border [&_input]:!border-gray-300 [&_input]:!focus:border-gray-300 [&_input]:!rounded [&_input]:!px-2 [&_input]:!py-1 [&_input]:!shadow-none [&_input]:!focus:shadow-none [&_input]:!bg-white [&_.line]:!hidden [&_.input-focus-group]:!shadow-none"
            value={editingLabel()}
            onInput={handleLabelChange}
            onBlur={handleLabelInputBlur}
            onInputRef={(ref) => addKeyDownListener(ref)}
          />
        </div>
      }
    >
      <div class="flex-1 min-w-0 flex items-center justify-between">
        <div
          data-testid="editable-title-label"
          class="text-lg font-semibold px-2 cursor-pointer truncate min-w-0"
        >
          {props.label ?? t('measurements.measurementData')}
        </div>

        <Show when={!props.hideTitleEdit}>
          <ActionButton
            testId="edit-icon"
            icon="tc-icon-edit"
            title={
              props.disableEdit
                ? (props.disabledReason ?? '')
                : t('general.edit')
            }
            onClick={handleEditButtonClick}
            disabled={props.disableEdit}
          />
        </Show>
      </div>
    </Show>
  );
};
